import { useState, useEffect } from 'react';
import { Box, Container, Stack, Typography, useTheme } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';

import useTable from 'hooks/useTable';
import useNotification from 'hooks/useNotification';
import MuiDataGrid from 'components/MuiDataGrid';
import { FormProps, IEtcImages } from '../PatientList/api/type';
import ListActionButtons from './ListActionButtons';
import EtcDialog from './EtcDialog';
import { updateMiscHidden } from 'apis/admin';

interface Props extends FormProps {
  defaultList?: IEtcImages[];
}

const EtcList = (props: Props) => {
  const { setValue, defaultList } = props;
  const { palette } = useTheme();
  const { onSuccess, onFail } = useNotification();

  const { list, setList } = useTable<IEtcImages>();
  const [defaultValue, setDefaultValue] = useState<Partial<IEtcImages> | null>(
    null
  );

  useEffect(
    () =>
      setList(
        defaultList?.map((item: any) => {
          const miscellaneousImages = Array.from(
            { length: 5 },
            (_, i) => item[`miscellaneousImage${i + 1}`]
          ).filter(item => item);
          return {
            ...item,
            miscellaneousImages,
          };
        }) ?? []
      ),
    [defaultList, setList]
  );
  useEffect(() => {
    setValue(
      'miscellaneous',
      list.map(item => {
        // 저장할 영상 이미지 파일들을 key 값에 맞게 object 재구성
        const images = item.miscellaneousImages?.reduce((prev, next, index) => {
          if (!next) return prev;
          return { ...prev, [`miscellaneousImage${index + 1}`]: next };
        }, {});

        // api 업데이트할 형태로 오브젝트를 저장함
        return {
          ...item,
          ...images,
        };
      })
    );
  }, [list, setValue]);

  const onCreateList = (data: IEtcImages) => setList(list.concat(data));
  const onUpdateList = (data: IEtcImages) =>
    setList(
      list.map(item =>
        item.ptMiscellaneousNo === data.ptMiscellaneousNo ? data : item
      )
    );

  const ActionButtons = ({ row }: { row: IEtcImages }) => {
    const onUpdate = () => setDefaultValue(row);

    const onToggleHidden = async () => {
      const nextHidden = !row.isHidden;

      try {
        await updateMiscHidden(row.ptMiscellaneousUuid);

        setList(prev =>
          prev.map(item =>
            item.ptMiscellaneousNo === row.ptMiscellaneousNo
              ? { ...item, isHidden: nextHidden }
              : item
          )
        );

        onSuccess(
          nextHidden ? '숨김 처리되었습니다.' : '숨김이 해제되었습니다.'
        );
      } catch (error) {
        console.error('숨김 처리 중 오류 발생:', error);
        onFail(
          nextHidden
            ? '숨김 처리에 실패하였습니다.'
            : '숨김 해제에 실패하였습니다.',
          error
        );
      }
    };

    const onDelete = () =>
      setList(prev =>
        prev.filter(item => item.ptMiscellaneousNo !== row.ptMiscellaneousNo)
      );

    return (
      <ListActionButtons
        onUpdate={onUpdate}
        onDelete={onDelete}
        onToggleHidden={onToggleHidden}
        isHidden={row.isHidden}
      />
    );
  };

  const columns: GridColDef[] = [
    { field: 'ptMiscellaneousNo', headerName: '기록지번호' },
    { field: 'ptMiscellaneousItem', headerName: '기록지명' },
    // { field: 'feeKor', headerName: '검사명(한)' },
    {
      align: 'right',
      field: 'actions',
      headerName: '',
      renderCell: ({ row }) => <ActionButtons row={row} />,
    },
  ];

  return (
    <Container
      maxWidth={false}
      disableGutters
      component="section"
      sx={{ background: '#fff', p: 2 }}
    >
      <Stack spacing={2}>
        <Box display="flex" alignItems="center">
          <Typography variant="h5" sx={{ color: `${palette.primary.main}` }}>
            기타기록지 리스트 (수정가능)
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 400,
              color: '#555555a4',
              marginLeft: '30px',
            }}
          >
            (추가 시 스마트널스 ENR로 문의해주세요.)
          </Typography>
          {/* <Button
            size="small"
            variant="outlined"
            onClick={onCreateStart}
            sx={{ ml: 3 }}
          >
            추가
          </Button> */}
        </Box>

        <MuiDataGrid
          columns={columns}
          rows={list.map(item => ({ ...item, id: item.ptMiscellaneousNo }))}
        />
      </Stack>

      {defaultValue && (
        <EtcDialog
          isUpdate={defaultValue.ptMiscellaneousNo ? true : false}
          defaultValue={defaultValue}
          onClose={() => setDefaultValue(null)}
          onSearchNo={(no: number) =>
            list.find(item => item.ptMiscellaneousNo === no) ? true : false
          }
          onSave={defaultValue.ptMiscellaneousNo ? onUpdateList : onCreateList}
        />
      )}
    </Container>
  );
};

export default EtcList;
